import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import { ETouchType } from 'types/touch-type';

import {
  ButtonBase,
  Grid,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import EditRoundedIcon from '@material-ui/icons/EditRounded';

import Button from 'creative-components/CustomButtons/Button';

import ReuseTouchOptionCard from 'components/CampaignSteps/CustomTouches/ReuseTouchOptionCard';
import PreviewEmailModal from 'components/CampaignSteps/PreviewEmailModal';
import PreviewPostcardLetterModal from 'components/CampaignSteps/PreviewPostcardLetterModal';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { reuseCampaignExtraTouchSource } from 'utils/api';
import {
  enumToReadableName,
  openFileInNewWindow,
  showAPIErrorAlert,
} from 'utils/lib';
import { type TouchPreviewData, DocumentTouchPreviewData, EmailTouchPreviewData } from 'utils/touch-preview';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '32px 40px 40px',
    minWidth: '760px',
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      padding: '24px 20px 28px',
    },
  },
  headline: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '22px',
    lineHeight: '30px',
    marginBottom: '10px',
  },
  body: {
    color: theme.palette.darkGray.main,
    fontSize: '16px',
    lineHeight: '24px',
    marginBottom: '28px',
  },
  optionGrid: {
    marginBottom: '24px',
  },
  optionCard: {
    width: '100%',
    borderRadius: '18px',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    backgroundColor: theme.palette.grayScale8.main,
    padding: '24px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    transition: 'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 14px 30px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  optionIcon: {
    marginBottom: '16px',
  },
  optionTitle: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '24px',
    marginBottom: '10px',
  },
  optionDescription: {
    color: theme.palette.darkGray.main,
    fontSize: '15px',
    lineHeight: '22px',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '16px',
  },
  sectionHeader: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '22px',
    lineHeight: '28px',
  },
  helperText: {
    color: theme.palette.darkGray.main,
    fontSize: '15px',
    lineHeight: '22px',
    marginBottom: '24px',
  },
  cardGrid: {
    marginBottom: '24px',
  },
  touchCard: {
    width: '100%',
    minHeight: '220px',
    borderRadius: '18px',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    backgroundColor: theme.palette.grayScale8.main,
    padding: '20px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  },
  touchCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 14px 30px rgba(86, 145, 127, 0.16)',
  },
  touchCardTitle: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '24px',
    marginBottom: '8px',
  },
  touchCardTitleDate: {
    color: theme.palette.grayScale11.main,
    fontSize: '15px',
    fontWeight: 500,
  },
  touchCardThumbnail: {
    marginTop: '18px',
    width: '100%',
    height: '200px',
    borderRadius: 0,
    backgroundColor: 'transparent',
    overflow: 'visible',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    '&[disabled]': {
      cursor: 'default',
    },
  },
  touchCardThumbnailViewport: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'visible',
    margin: '0 auto',
  },
  touchCardThumbnailCanvas: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transformOrigin: 'center center',
  },
  touchCardThumbnailFrame: {
    border: 0,
    display: 'block',
    pointerEvents: 'none',
    backgroundColor: 'transparent',
  },
  touchCardThumbnailLoading: {
    padding: '12px',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    justifyContent: 'center',
  },
  touchCardThumbnailError: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    textAlign: 'center',
    color: theme.palette.grayScale11.main,
    fontSize: '13px',
    lineHeight: '18px',
  },
  touchCardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginTop: '20px',
  },
  touchCardSelectButton: {
    minHeight: '40px',
    borderRadius: '12px',
    padding: '10px 16px',
    color: theme.palette.darkGray.main,
    backgroundColor: theme.palette.primary.light,
    fontWeight: 600,
    textTransform: 'uppercase',
    '&[disabled]': {
      opacity: 0.5,
    },
  },
  touchCardSelectButtonSelected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.white.main,
  },
  touchCardPreviewButton: {
    color: theme.palette.grayScale11.main,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  bottomActions: {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  emptyState: {
    color: theme.palette.darkGray.main,
    fontSize: '15px',
    lineHeight: '22px',
    padding: '16px 0 8px',
  },
}));

const getReuseAvailability = (touchTrigger: any) => {
  if (touchTrigger.requiresCustomTouchTemplate && !touchTrigger.customTouchTemplate) {
    return false;
  }

  return true;
};

interface Props {
  touchTrigger: any;
  campaignTouchTriggers: any[];
  onReceiveEditedTouchTrigger: (touchTrigger: any) => void;
  onCreateNewContent: () => void;
}

const ChooseExtraTouchContent = ({
  touchTrigger,
  campaignTouchTriggers,
  onReceiveEditedTouchTrigger,
  onCreateNewContent,
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const [view, setView] = React.useState<'CHOOSE' | 'REUSE'>('CHOOSE');
  const [selectedSourceTouchTriggerId, setSelectedSourceTouchTriggerId] = React.useState<string | null>(null);
  const [previewTouchTrigger, setPreviewTouchTrigger] = React.useState<any | null>(null);
  const [previewTouchData, setPreviewTouchData] = React.useState<TouchPreviewData | null>(null);

  const candidateTouchTriggers = React.useMemo(() => campaignTouchTriggers
    .filter((candidateTouchTrigger) => candidateTouchTrigger._id !== touchTrigger._id)
    .filter((candidateTouchTrigger) => candidateTouchTrigger.touchType.name === touchTrigger.touchType.name)
    .filter((candidateTouchTrigger) => getReuseAvailability(candidateTouchTrigger))
    .sort((a, b) => moment(a.sendAt).valueOf() - moment(b.sendAt).valueOf()), [campaignTouchTriggers, touchTrigger._id, touchTrigger.touchType.name]);

  const selectedSourceTouchTrigger = candidateTouchTriggers.find((candidateTouchTrigger) => candidateTouchTrigger._id === selectedSourceTouchTriggerId) ?? null;

  const openPreview = React.useCallback((candidateTouchTrigger: any, cachedPreview?: TouchPreviewData) => {
    if (cachedPreview?.type === 'pdf' && cachedPreview.pdf) {
      openFileInNewWindow(cachedPreview.pdf);
      return;
    }

    setPreviewTouchTrigger(candidateTouchTrigger);
    setPreviewTouchData(cachedPreview ?? null);
  }, []);

  const onConfirmReuse = async () => {
    if (!selectedSourceTouchTriggerId) return;

    showLoadingIndicatorModal();

    try {
      const { touchTrigger: updatedTouchTrigger } = await reuseCampaignExtraTouchSource(touchTrigger._id, selectedSourceTouchTriggerId);
      onReceiveEditedTouchTrigger(updatedTouchTrigger);
      toast.success(`${enumToReadableName(touchTrigger.touchType.name)} content reused successfully.`);
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    } finally {
      hideLoadingIndicatorModal();
    }
  };

  return (
    <>
      {previewTouchTrigger?.touchType?.name === ETouchType.Email && (
        <PreviewEmailModal
          onClose={() => {
            setPreviewTouchTrigger(null);
            setPreviewTouchData(null);
          }}
          initialPreviewData={previewTouchData as EmailTouchPreviewData}
          touchTrigger={previewTouchTrigger}
        />
      )}

      {previewTouchTrigger && [ETouchType.Postcard, ETouchType.Letter].includes(previewTouchTrigger?.touchType?.name) && (
        <PreviewPostcardLetterModal
          onClose={() => {
            setPreviewTouchTrigger(null);
            setPreviewTouchData(null);
          }}
          initialPreviewData={previewTouchData as DocumentTouchPreviewData}
          touchTrigger={previewTouchTrigger}
        />
      )}

      <div className={classes.container}>
        {view === 'CHOOSE' && (
          <>
            <div className={classes.headline}>Choose how you want to configure this extra touch</div>
            <div className={classes.body}>
              You can reuse one of your existing campaign touches, or start fresh and customize this extra touch on its own.
            </div>

            <Grid container spacing={3} className={classes.optionGrid}>
              <Grid item xs={12} md={6}>
                <ButtonBase className={classes.optionCard} onClick={() => setView('REUSE')}>
                  <CheckCircleRoundedIcon className={classes.optionIcon} htmlColor={theme.palette.primary.main} />
                  <div className={classes.optionTitle}>Reuse from campaign</div>
                  <div className={classes.optionDescription}>
                    Pick an existing
                    {' '}
                    {enumToReadableName(touchTrigger.touchType.name).toLowerCase()}
                    {' '}
                    touch from this campaign and use that same design for this extra touch.
                  </div>
                </ButtonBase>
              </Grid>

              <Grid item xs={12} md={6}>
                <ButtonBase className={classes.optionCard} onClick={onCreateNewContent}>
                  <EditRoundedIcon className={classes.optionIcon} htmlColor={theme.palette.darkGray.main} />
                  <div className={classes.optionTitle}>Create new content</div>
                  <div className={classes.optionDescription}>
                    Start fresh and customize this extra touch with new text or a new uploaded design.
                  </div>
                </ButtonBase>
              </Grid>
            </Grid>
          </>
        )}

        {view === 'REUSE' && (
          <>
            <div className={classes.sectionHeaderRow}>
              <div className={classes.sectionHeader}>Choose a touch to reuse</div>
              <Button color="white" round onClick={() => setView('CHOOSE')}>
                <ArrowBackRoundedIcon />
                Back
              </Button>
            </div>

            <div className={classes.helperText}>
              Select an existing
              {' '}
              {enumToReadableName(touchTrigger.touchType.name).toLowerCase()}
              {' '}
              touch from this campaign. You can preview any option before using it.
            </div>

            {candidateTouchTriggers.length === 0 && (
              <div className={classes.emptyState}>
                There are no other
                {' '}
                {enumToReadableName(touchTrigger.touchType.name).toLowerCase()}
                {' '}
                touches in this campaign yet. Create new content instead.
              </div>
            )}

            <Grid container spacing={3} className={classes.cardGrid}>
              {candidateTouchTriggers.map((candidateTouchTrigger) => {
                const isSelected = candidateTouchTrigger._id === selectedSourceTouchTriggerId;

                return (
                  <Grid item xs={12} md={6} key={candidateTouchTrigger._id}>
                    <ReuseTouchOptionCard
                      candidateTouchTrigger={candidateTouchTrigger}
                      classes={classes}
                      isSelected={isSelected}
                      shouldPreload={view === 'REUSE'}
                      onOpenPreview={openPreview}
                      onSelect={setSelectedSourceTouchTriggerId}
                    />
                  </Grid>
                );
              })}
            </Grid>

            <div className={classes.bottomActions}>
              <Button
                color="primary"
                round
                disabled={!selectedSourceTouchTrigger}
                onClick={onConfirmReuse}
              >
                Reuse Selected Touch
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ChooseExtraTouchContent;
